import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RELEVANCE_META } from '@/lib/changeDetection';
import type { ChangeEvent } from '@/lib/types';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export function ChangeCard({ change }: { change: ChangeEvent }) {
  const [open, setOpen] = useState(false);
  const rel = RELEVANCE_META[change.relevance];

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-xl border border-border/60 bg-card/60 transition-colors hover:border-border">
        <CollapsibleTrigger className="flex w-full items-start gap-3 p-4 text-left">
          <span className="text-xl leading-none">{change.emoji}</span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold">{change.title}</p>
              <span className={cn('text-xs', rel.className)}>{rel.emoji}</span>
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">
              {change.whatHappened}
            </p>
          </div>
          <ChevronDown
            className={cn(
              'mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3 border-t border-border/60 px-4 py-3 text-sm">
            <Field label="What happened?" value={change.whatHappened} />
            <Field label="Why does it matter?" value={change.whyItMatters} />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                BCH relevance
              </p>
              <p className={cn('mt-0.5 font-medium', rel.className)}>
                {rel.emoji} {rel.label} — not confirmed.
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                What would confirm it?
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 font-mono text-xs">
                {change.watchNext.map((w, i) => (
                  <span key={w} className="flex items-center gap-1.5">
                    <span className="rounded bg-secondary px-1.5 py-0.5">{w}</span>
                    {i < change.watchNext.length - 1 && (
                      <span className="text-muted-foreground">→</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 text-foreground/90">{value}</p>
    </div>
  );
}
